import { Feature, MultiPolygon } from 'geojson';
import { MRT_ColumnFiltersState } from 'mantine-react-table';

import { ContractIlotFieldLayer } from '../../lib/types/contract-ilot-fields';
import { ParcellesStackField } from '../../components/parcelles-field-popup/parcelles-field-popup';

export interface ParcellesTableRow {
  id: number;
  area: number | null;
  telepac_code: string | null;
  campaign_year: number;
  campaign_id: number;
  layer_color: string;
  identification_label: string;
  identification_source: 'TELEPAC' | 'RPG';
  reference_campaign_year: number | null;
}

export function buildTableRows(layers: ContractIlotFieldLayer[]): ParcellesTableRow[] {
  const rows: ParcellesTableRow[] = [];
  for (const layer of layers) {
    for (const feature of layer.fields.features) {
      rows.push({
        id: feature.properties.id,
        area: feature.properties.area,
        telepac_code: feature.properties.telepac_code,
        campaign_year: layer.campaign_year,
        campaign_id: layer.campaign_id,
        layer_color: layer.color,
        identification_label: feature.properties.identification_label,
        identification_source: feature.properties.identification_source,
        reference_campaign_year: feature.properties.reference_campaign_year,
      });
    }
  }
  return rows;
}

export function filterTableRows(
  rows: ParcellesTableRow[],
  columnFilters: MRT_ColumnFiltersState,
): ParcellesTableRow[] {
  if (!columnFilters.length) {
    return rows;
  }

  return rows.filter((row) =>
    columnFilters.every((filter) => {
      const rawValue = row[filter.id as keyof ParcellesTableRow];
      const filterValue = filter.value;

      if (filterValue == null || filterValue === '') {
        return true;
      }

      return String(rawValue ?? '')
        .toLowerCase()
        .includes(String(filterValue).toLowerCase());
    }),
  );
}

export function buildFilteredLayers(
  layers: ContractIlotFieldLayer[],
  allowedFieldIds: ReadonlySet<number>,
): ContractIlotFieldLayer[] {
  const filtered: ContractIlotFieldLayer[] = [];

  for (const layer of layers) {
    const features = layer.fields.features.filter((feature) =>
      allowedFieldIds.has(feature.properties.id),
    );
    if (!features.length) {
      continue;
    }

    let totalArea = 0;
    for (const feature of features) {
      totalArea += feature.properties.area ?? 0;
    }

    filtered.push({
      ...layer,
      fields: {
        ...layer.fields,
        features,
      },
      stats: {
        count: features.length,
        total_area: totalArea,
      },
    });
  }

  return filtered;
}

export function buildFieldFeatureIndex(
  layers: ContractIlotFieldLayer[],
): Map<number, Feature<MultiPolygon>> {
  const index = new Map<number, Feature<MultiPolygon>>();
  for (const layer of layers) {
    for (const feature of layer.fields.features) {
      index.set(feature.properties.id, feature);
    }
  }
  return index;
}

export function buildStackFieldFromRow(row: ParcellesTableRow): ParcellesStackField {
  return {
    id: row.id,
    area: row.area,
    campaignYear: row.campaign_year,
    layerColor: row.layer_color,
    telepacCode: row.telepac_code,
    identificationLabel: row.identification_label,
    identificationSource: row.identification_source,
    referenceCampaignYear: row.reference_campaign_year,
  };
}
